import React, {FC, useEffect, useState} from "react"
import {Body, H6} from "@irati/ui/src/Typography"
import Card from "@irati/ui/src/Layout/Card"
import theme from "@irati/ui/src/global/theme"
import Flex from "@irati/ui/src/Flex"
import styled from "styled-components"
import {FormattedMessage} from "@irati/utils/lang"
import {formatCurrency} from "@irati/utils/currency/formatCurrency"
import {TiersResponse} from "@irati/types"
import topupService from "@irati/wallet/services/TopUp"

const StyledH6 = styled(H6)`
  background-color: ${theme.colors.secondary1};
  border-bottom-left-radius: ${theme.spacing.one};
  border-bottom-right-radius: ${theme.spacing.one};
  color: ${theme.colors.greys.black};
`

interface Props {
  purchaseCurrency: string
  serviceFeesTiers: TiersResponse[]
}

const calculateSaveFee = (serviceFeesTiers, index) => {
  const {endValue, tierServiceFeeRate, baseServiceFeeRate} = serviceFeesTiers[index]
  return index < serviceFeesTiers.length - 1 ? endValue * (baseServiceFeeRate - tierServiceFeeRate) : 0
}
const ServiceFeesCards: FC<Props> = ({purchaseCurrency, serviceFeesTiers}) => {
  const [waivedTiers, setWaivedTiers] = useState(false)

  useEffect(() => {
    topupService.serviceFeesWaived().then((serviceFeeWaived) => setWaivedTiers(serviceFeeWaived))
  }, [])

  return (
    <Card small bgColor={theme.colors.greys.graphGrey}>
      {waivedTiers && <Body color={theme.colors.greys.black} marginBottom={2}>
        <FormattedMessage id="wallet.reward.admin.topup.serviceFeeOffer" />
      </Body>}

      <Flex gap={theme.spacing.two} wrap="wrap">
        {serviceFeesTiers.map(({baseServiceFee, tierServiceFee, startValue, endValue, baseServiceFeeRate, tierServiceFeeRate}, index) => {
          const maxSaveTierFee = calculateSaveFee(serviceFeesTiers, index)
          const discounted = tierServiceFeeRate < baseServiceFeeRate
          return (
            <Card key={endValue} flush>
              <Flex direction="column" justifyContent="center" minWidth="172px" height="100%">
                <Flex direction="column" alignItems="center" padding={2}>
                  <Flex marginBottom>
                    <Body textDecoration={discounted ? "line-through" : ""} marginRight={0.5} color={theme.colors.greys.black}>
                      {discounted ? baseServiceFee : tierServiceFee}%
                    </Body>
                    {discounted && <Body color={theme.colors.tertiary.g_600}>{tierServiceFee}%</Body>}
                  </Flex>
                  {endValue === -1 ? (
                    <Body>{formatCurrency({currencyCode: purchaseCurrency, value: startValue - 100})}+</Body>
                  ) : (
                    <Body>
                      {formatCurrency({currencyCode: purchaseCurrency, value: startValue})}
                      &nbsp;-&nbsp;
                      {formatCurrency({currencyCode: purchaseCurrency, value: endValue})}
                    </Body>
                  )}
                </Flex>

                {index !== serviceFeesTiers.length - 1 && maxSaveTierFee > 0 && (
                  <StyledH6 textAlign="center" paddingTop={0.5} paddingBottom={0.5}>
                    <FormattedMessage
                      id="wallet.reward.admin.card.topup.serviceFeeTier.saveUpTo"
                      values={{value: formatCurrency({value: maxSaveTierFee, currencyCode: purchaseCurrency})}}
                    />
                  </StyledH6>
                )}

                {/* Last tier */}
                {index !== 0 && index === serviceFeesTiers.length - 1 && serviceFeesTiers.length > 1 && (
                  <StyledH6 textAlign="center" paddingTop={0.5} paddingBottom={0.5}>
                    <FormattedMessage id="wallet.reward.admin.card.topup.serviceFeeTier.bestValue" />
                  </StyledH6>
                )}
              </Flex>
            </Card>
          )
        })}
      </Flex>
    </Card>
  )
}

export default ServiceFeesCards
